import { ChangeDetectorRef, Component, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import * as _ from 'underscore';
import { AliasList } from '../../../models/alias-lists';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { CommonService } from '../../../services/common/common.service';
import { Comparison } from '../../../models/comparison';
import { Connector } from '../../../models/connector';
import { Corpus } from '../../../models/corpus';
import { Custodian } from '../../../models/custodian';
import { DataArea } from '../../../models/data-area';
import { DomainList } from '../../../models/domain-lists';
import { DynamicFolder } from '../../../models/dynamic-folder';
import { Endpoint, EntityType, HelpIndex, MessageType, UpdateStatusType } from '../../../models/enums';
import { ExcludedContent } from '../../../models/excluded-content';
import { ExportDb } from '../../../models/export-database';
import { ExportMetadataProfile } from '../../../models/export-metadata-profile';
import { ExportStream } from '../../../models/export-stream';
import { ExportVolume } from '../../../models/export-volume';
import { Group } from '../../../models/group';
import { RealmNode } from '../../../models/realm-node';
import { KeyFile } from '../../../models/key-file';
import { LoadFileProjectTemplate } from '../../../models/load-file-project-template';
import { Pattern } from '../../../models/pattern';
import { Profile } from '../../../models/profile';
import { Project, ProjectAction } from '../../../models/project';
import { RestService } from '../../../services/rest/rest.service';
import { SavedSearch } from '../../../models/saved-searches';
import { Tag, TagQueueApplication } from '../../../models/tag';
import { Task } from '../../../models/task';
import { Template } from '../../../models/template';
import { User } from '../../../models/user';
import { Workflow } from '../../../models/workflow';
import { Role } from '../../../models/role';
import { ViewType } from '../../../models/view';
import { MediaID } from '../../../models/mediaid';
import { Batch } from '../../../models/batch';
import { SysOrganization } from '../../../models/system-organization';
import { Service } from '../../../models/service';
import { Storage } from '../../../models/storage';

class DeleteEntityDataType {
    entity: any;
    entityType: string;
    request: any;
    endpoint: Endpoint;
    title: string;
    isBusyMessage: string;
    message: string;
    contentTemplate?: TemplateRef<any> = null;
    okLabel = 'Delete';
    helpIndex?: HelpIndex;
    updateStatusType?: UpdateStatusType;
    useWarningStyle = false;

    constructor(value?: Partial<DeleteEntityDataType>) {
        if (value) {
            Object.assign(this, value);

            let capitalizedTitle = this.entityType.replace(/\b\w/g, l => l.toUpperCase());
            capitalizedTitle = (this.entity?.viewType === ViewType.TAG || this.entity instanceof Tag) ? "Tag" : (this.entity instanceof Profile) ? 'Synthetic Document' : capitalizedTitle;

            if (!this.title) {
                this.title = `Delete ${capitalizedTitle}`;
            }
            if (!this.isBusyMessage) {
                this.isBusyMessage = `Deleting ${capitalizedTitle} ...`;
            }
            if (!this.message) {
                if (this.entity.name) {
                    this.message = (this.entity.viewType === ViewType.TAG || this.entity instanceof Tag) && this.entity.documentCount > 0 ? 'Deleting "' + this.entity.name + '"' + ' will also remove "' + this.entity.name + '" tag from ' + this.entity.documentCount + ' documents.'
                        : 'Delete "' + this.entity.name + '"?';
                } else {
                    this.message = 'Delete "' + this.entity.description + '"?';
                }
            }
        }
    }
}

@Component({
    standalone: false,
    selector: 'res-delete-entity',
    templateUrl: './delete-entity-modal.component.html',
    styleUrls: ['./delete-entity-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DeleteEntityModalComponent extends BaseModalComponent {
    entityData: DeleteEntityDataType = new DeleteEntityDataType();
    @ViewChild('customTemplate', { static: true }) customTemplate: TemplateRef<any>;

    isBusy = false;
    isBusyMessage = 'Loading...';
    modalAutomationId = 'delete-entity';
    modalTitle: string;
    secondaryButtonLabel = 'Cancel';
    modalClass = 'delete-entity-modal';
    modalHelpIndex: HelpIndex;
    viewType: string;
    sharedCorpora: any = [];

    constructor(
        private modalService: BsModalService,
        public deleteEntityRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService,
        private changeDetector: ChangeDetectorRef) {
        super(modalService, deleteEntityRef);
        this.loadEntityData();
    }

    loadEntityData(): void {
        if (this.modalData) {
            if (this.modalData instanceof Project) {
                this.restService.post(Endpoint.projectManager_listSharedCorpora, {
                    projectHandle: this.modalData.handle, organizationName: this.modalData.orgName, originatingProject: true
                }).then((response) => {
                    this.sharedCorpora = response.corpora;

                    this.entityData = new DeleteEntityDataType({
                        title: this.modalData?.projectAction === ProjectAction.APPROVE_PROJECT_DELETION ? 'Approve Project Deletion' : 'Delete Project',
                        entity: (this.modalData as Project),
                        entityType: 'project',
                        endpoint: this.modalData?.projectAction === ProjectAction.APPROVE_PROJECT_DELETION ? Endpoint.adminOrgManager_approveProjectDeleteRequest : (
                            this.options?.deleteProjectFromSystemSettings ? Endpoint.adminOrgManager_requestProjectDelete : Endpoint.orgManager_requestProjectDelete
                        ),
                        contentTemplate: this.customTemplate,
                        okLabel: this.modalData?.projectAction === ProjectAction.APPROVE_PROJECT_DELETION ? 'Approve Deletion' : (this.sharedCorpora.length > 0 ? 'Delete Project & Data Sets' : 'Request Project Deletion'),
                        updateStatusType: UpdateStatusType.PROJECT
                    });
                    this.modalTitle = this.entityData.title;
                    this.changeDetector.detectChanges();

                    if (this.modalData?.projectAction === ProjectAction.APPROVE_PROJECT_DELETION) {
                        Object.assign(this.entityData, {
                            request: {
                                handle: this.modalData?.projectHandle,
                                taskDescription: `Approving delete for project ${this.modalData?.name}`,
                                systemScope: true
                            }
                        });
                    } else {
                        Object.assign(this.entityData, {
                            request: {
                                projectHandle: (this.modalData as Project).handle,
                                taskDescription: `Delete Project ${(this.modalData as Project).name}`,
                                systemScope: this.options?.deleteProjectFromSystemSettings
                            }
                        });
                    }
                });
            } else if (this.modalData instanceof Corpus) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Corpus),
                    entityType: 'data set',
                    request: {
                        corpusHandle: (this.modalData as Corpus).handle,
                        projectHandle: this.commonService.currentProject.handle,
                        removeReferences: true,
                        taskDescription: `Delete Data Set ${(this.modalData as Corpus).name}`
                    },
                    endpoint: Endpoint.projectManager_removeCorpusFromProject,
                    updateStatusType: UpdateStatusType.CORPUS
                });
            } else if (this.modalData instanceof Custodian) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Custodian),
                    entityType: 'custodian',
                    request: {
                        handle: (this.modalData as Custodian).handle,
                        taskDescription: `Delete Custodian ${(this.modalData as Custodian).name}`
                    },
                    endpoint: Endpoint.ecaManager_deleteCustodian,
                    updateStatusType: UpdateStatusType.CUSTODIAN
                });
            } else if (this.modalData instanceof MediaID) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as MediaID),
                    entityType: EntityType.mediaID,
                    request: {
                        handle: (this.modalData as MediaID).handle,
                        taskDescription: `Delete MediaID ${(this.modalData as MediaID).name}`
                    },
                    endpoint: Endpoint.ecaManager_deleteMediaid,
                    updateStatusType: UpdateStatusType.MEDIAID
                })
            } else if (this.modalData instanceof Batch) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Batch),
                    entityType: EntityType.batch,
                    request: {
                        handle: (this.modalData as Batch).handle,
                        taskDescription: `Delete Batch ${(this.modalData as Batch).name}`
                    },
                    endpoint: Endpoint.ecaManager_deleteBatch,
                    updateStatusType: UpdateStatusType.BATCH
                })
            } else if (this.modalData instanceof DynamicFolder) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as DynamicFolder),
                    entityType: 'folder',
                    request: {
                        handle: (this.modalData as DynamicFolder).handle,
                        taskDescription: `Delete Folder ${(this.modalData as DynamicFolder).name}`
                    },
                    endpoint: Endpoint.projectManager_deleteDynamicFolder,
                    updateStatusType: UpdateStatusType.DYNAMIC_FOLDER_VIEW
                });
            } else if (this.modalData instanceof Connector) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Connector),
                    entityType: 'connector',
                    request: {
                        handle: (this.modalData as Connector).handle,
                        taskDescription: this.modalData.name
                    },
                    endpoint: Endpoint.orgManager_deleteConnector,
                    updateStatusType: UpdateStatusType.CONNECTOR
                });
            } else if (this.modalData instanceof Template) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Template),
                    entityType: 'template',
                    request: {
                        handle: (this.modalData as Template).handle,
                        taskDescription: `Delete Template ${(this.modalData as Template).name}`,
                        systemScope: (this.modalData as any).isSystemRole
                    },
                    endpoint: Endpoint.orgManager_deleteTemplate,
                    updateStatusType: UpdateStatusType.TEMPLATE_MEMEBERSHIP
                });
            } else if (this.modalData instanceof ExportMetadataProfile) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as ExportMetadataProfile),
                    entityType: 'template',
                    request: {
                        handle: (this.modalData as ExportMetadataProfile).handle,
                        taskDescription: `Remove Export Fields Profile ${(this.modalData as ExportMetadataProfile).name}`,
                        systemScope: (this.modalData as any).isSystemRole
                    },
                    endpoint: Endpoint.projectManager_deleteExportMetadataProfile,
                    updateStatusType: UpdateStatusType.TEMPLATE_MEMEBERSHIP
                });
            } else if (this.modalData instanceof LoadFileProjectTemplate) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as LoadFileProjectTemplate),
                    entityType: 'template',
                    request: {
                        handle: (this.modalData as LoadFileProjectTemplate).id,
                        taskDescription: `Remove Load File Profile ${(this.modalData as LoadFileProjectTemplate).name}`,
                        systemScope: (this.modalData as any).isSystemRole
                    },
                    endpoint: Endpoint.projectManager_deleteLoadFileMetadataProfile,
                    updateStatusType: UpdateStatusType.TEMPLATE_MEMEBERSHIP
                });
            } else if (this.modalData instanceof DataArea) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as DataArea),
                    entityType: 'data area',
                    endpoint: Endpoint.orgManager_deleteNFSDataArea,
                    updateStatusType: UpdateStatusType.DATA_AREA,
                    request: {
                        requestHandle: null,
                        attributes: [],
                        callbackEndpointUrl: '',
                        handle: (this.modalData as DataArea).handle,
                        taskDescription: `Remove Data Locator ${(this.modalData as DataArea).name}`
                    }
                });
            } else if (this.modalData instanceof Task) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Task),
                    entityType: 'task',
                    message: 'Delete "' + (this.modalData as Task).description + '"?',
                    endpoint: Endpoint.projectManager_deleteTasks,
                    updateStatusType: UpdateStatusType.TASK,
                    request: {
                        handles: [(this.modalData as Task).handle]
                    }
                });
            } else if (this.modalData instanceof Array) {
                if (this.modalData[0] instanceof Task) {
                    this.entityData = new DeleteEntityDataType({
                        entity: (this.modalData as Task[]),
                        entityType: 'task',
                        message: this.modalData.length === 1 ? 'Delete "' + (this.modalData[0] as Task).description + '"?' : 'Delete the ' + this.modalData.length + ' selected tasks?',
                        title: this.modalData.length === 1 ? 'Delete Task' : 'Delete Tasks',
                        endpoint: Endpoint.projectManager_deleteTasks,
                        updateStatusType: UpdateStatusType.TASK,
                        request: {
                            handles: _.pluck(this.modalData, 'handle')
                        }
                    });
                } else {
                    console.error('Attempting to delete an unknown array.  Configure delete-entity-modal.component.ts to enable this.');
                }
            } else if (this.modalData instanceof Pattern) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Pattern),
                    entityType: 'pattern',
                    endpoint: Endpoint.projectManager_deleteRegex,
                    request: {
                        handle: (this.modalData as Pattern).handle,
                        systemScope: (this.modalData as any).systemScope
                    },
                    updateStatusType: UpdateStatusType.PATTERN
                });
            } else if (this.modalData instanceof ExportDb) {
                const exportDbToDelete = this.modalData as ExportDb;
                this.entityData = new DeleteEntityDataType({
                    entity: exportDbToDelete,
                    entityType: 'export database',
                    endpoint: Endpoint.orgManager_deleteExportDatabaseConnection,
                    updateStatusType: UpdateStatusType.EXPORT_DATABASE,
                    request: {
                        handle: exportDbToDelete.handle,
                        taskDescription: `Remove export database connection ${exportDbToDelete.name}`
                    }
                });
            } else if (this.modalData instanceof AliasList) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as AliasList),
                    entityType: 'alias list',
                    endpoint: Endpoint.projectManager_deleteAliasList,
                    request: {
                        handle: (this.modalData as AliasList).handle
                    },
                    updateStatusType: UpdateStatusType.ALIAS_LISTS
                });
            } else if (this.modalData instanceof DomainList) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as DomainList),
                    entityType: 'domain list',
                    endpoint: Endpoint.projectManager_deleteDomain,
                    request: {
                        handle: (this.modalData as DomainList).handle,
                        systemScope: (this.modalData as any).isSystemUser
                    },
                    updateStatusType: UpdateStatusType.DOMAIN_MEMBERSHIP
                });
            } else if (this.modalData instanceof ExcludedContent) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as ExcludedContent),
                    entityType: 'excluded content',
                    endpoint: Endpoint.projectManager_deleteEmailSignature,
                    request: {
                        handle: (this.modalData as ExcludedContent).handle,
                        systemScope: (this.modalData as any).systemScope
                    },
                    updateStatusType: UpdateStatusType.EXCLUDED_CONTENT
                });
            } else if (this.modalData instanceof KeyFile) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as KeyFile),
                    entityType: 'key file',
                    endpoint: Endpoint.orgManager_deleteLotusNotesIdFile,
                    request: {
                        handle: (this.modalData as KeyFile).handle,
                        taskDescription: `Deleting Lotus Notes ID file ${(this.modalData as KeyFile).name}`
                    },
                    updateStatusType: UpdateStatusType.KEY_FILE
                });
            } else if (this.modalData instanceof Tag) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Tag),
                    entityType: 'tags',
                    endpoint: Endpoint.projectManager_deleteTag,
                    updateStatusType: UpdateStatusType.TAG_VIEW,
                    request: {
                        tagHandle: (this.modalData as Tag).handle,
                        ownerHandle: (this.modalData as Tag).ownerHandle,
                        systemScope: (this.modalData as any).systemScope,
                    }
                });

                this.viewType = ViewType.TAG;
            } else if (this.modalData instanceof User) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as User),
                    entityType: 'user',
                    message: 'Delete "' + (this.modalData as User).userName + '"?',
                    endpoint: (this.modalData as User).isSuperSystemUser ? Endpoint.adminOrgManager_deleteUser : Endpoint.orgManager_deleteUser,
                    request: {
                        systemScope: (this.modalData as User).isSuperSystemUser,
                        organizationName: (this.modalData as User).customerName,
                        userName: (this.modalData as User).userName
                    },
                    updateStatusType: UpdateStatusType.USER,
                    title: (this.modalData as any)?.title
                });
            } else if (this.modalData instanceof Group) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Group),
                    entityType: 'groups',
                    endpoint: Endpoint.orgManager_deleteGroup,
                    request: {
                        handle: (this.modalData as Group).handle,
                        taskDescription: 'Deleting Group ' + (this.modalData as Group).handle,
                        systemScope: (this.modalData as any).isSystemContext
                    },
                    updateStatusType: UpdateStatusType.GROUP_VIEW,
                    title: (this.modalData as any)?.title
                });
            } else if (this.modalData instanceof RealmNode) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as RealmNode),
                    entityType: 'nodes',
                    endpoint: Endpoint.realmManager_deleteNode,
                    request: {
                        handle: (this.modalData as RealmNode).handle,
                        taskDescription: 'Deleting Node ' + (this.modalData as RealmNode).handle,
                    },
                    updateStatusType: UpdateStatusType.REALM_NODE_VIEW,
                    title: 'Confirm Deletion',
                    message: `<i class="fa fa-exclamation-triangle color-tan-07"></i>&nbsp;&nbsp;Delete "${(this.modalData as RealmNode).name}"? This action cannot be undone.`,
                    okLabel: 'Delete Node',
                });
            } else if (this.modalData instanceof Profile) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Profile),
                    entityType: 'profiles',
                    endpoint: Endpoint.projectManager_deleteProfile,
                    updateStatusType: UpdateStatusType.PROFILE,
                    request: {
                        handle: (this.modalData as Profile).handle,
                        taskDescription: 'Deleting Synthetic Document ' + (this.modalData as Profile).name
                    }
                });
            } else if (this.modalData instanceof Comparison) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Comparison),
                    entityType: 'comparison',
                    endpoint: Endpoint.projectManager_deleteComparison,
                    updateStatusType: UpdateStatusType.COMPARISON,
                    request: {
                        handle: (this.modalData as Comparison).handle,
                        taskDescription: 'Deleting Comparison ' + (this.modalData as Comparison).name
                    }
                });
            } else if (this.modalData instanceof SavedSearch) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as SavedSearch),
                    entityType: 'saved Search',
                    endpoint: Endpoint.projectManager_deleteActiveRule,
                    updateStatusType: UpdateStatusType.SAVED_SEARCH,
                    request: {
                        handle: (this.modalData as SavedSearch).handle,
                        taskDescription: 'Deleting Saved Search'
                    }
                });
            } else if (this.modalData instanceof TagQueueApplication) {
                // This is a special case and doesn't use UpdateStatusType.
                this.entityData = new DeleteEntityDataType({
                    title: 'Clear Changes Queue',
                    message: 'Delete ALL changes in the Queue?',
                    entityType: 'tagApplication'
                });
            } else if (this.modalData instanceof Workflow) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Workflow),
                    entityType: 'workflow',
                    message: 'Delete "' + (this.modalData as Workflow).name + '"?',
                    endpoint: this.options?.templateHandle ? Endpoint.workflowManager_deleteWorkflowFromTemplate : Endpoint.workFlowManager_deleteWorkFlow,
                    updateStatusType: UpdateStatusType.WORK_FLOW,
                    okLabel: this.options?.templateHandle ? 'Delete Workflow' : 'Delete',
                    request: this.options?.templateHandle ? { workflowHandle: (this.modalData as Workflow).handle, templateHandle: this.options.templateHandle } : {
                        handle: (this.modalData as Workflow).handle,
                    },
                    title: this.options?.title || ''
                });
            } else if (this.modalData instanceof ExportStream) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as ExportStream),
                    entityType: 'export stream',
                    endpoint: Endpoint.workingSetManager_deleteView,
                    updateStatusType: UpdateStatusType.EXPORT_VIEW,
                    request: {
                        handle: (this.modalData as ExportStream).handle,
                        taskDescription: `Delete Export ${(this.modalData as ExportStream).name}`
                    }
                });
            } else if (this.modalData instanceof ExportVolume) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as ExportVolume),
                    entityType: 'export volume',
                    endpoint: Endpoint.workingSetManager_deleteView,
                    updateStatusType: UpdateStatusType.EXPORT_VIEW,
                    request: {
                        handle: (this.modalData as ExportVolume).handle,
                        taskDescription: `Delete Export Volume ${(this.modalData as ExportVolume).name}`
                    }
                });
            } else if (this.modalData instanceof Role) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Role),
                    entityType: 'role',
                    updateStatusType: UpdateStatusType.TEMPLATE_MEMEBERSHIP,
                    endpoint: Endpoint.orgManager_deleteRole,
                    request: {
                        handle: (this.modalData as Role).handle,
                        systemScope: this.modalData.isSystemRole
                    }
                });
            } else if (this.modalData.viewType === ViewType.TAG) {
                const tagHandle = this.modalData.attributes ? this.modalData.attributes.find(a => a.name === 'tagHandle') : null;
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Tag),
                    entityType: 'tags',
                    endpoint: Endpoint.projectManager_deleteTag,
                    updateStatusType: UpdateStatusType.TAG_VIEW,
                    request: {
                        tagHandle: tagHandle ? tagHandle.value : null,
                        ownerHandle: this.commonService.currentProject.handle
                    }
                });
            } else if (this.modalData instanceof SysOrganization) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as SysOrganization),
                    entityType: 'organization',
                    request: {
                        handle: (this.modalData as SysOrganization).handle,
                        taskDescription: `Delete Organization ${(this.modalData as SysOrganization).name}`,
                        systemScope: this.options?.deleteOrganizationsFomSystemSettings
                    },
                    endpoint: Endpoint.realmManager_deleteOrganization,
                    message: `Delete "` + (this.modalData as SysOrganization).name + `"? This action cannot be undone.`,
                    okLabel: 'Delete Organization',
                    updateStatusType: UpdateStatusType.ORGANIZATION,
                    useWarningStyle: true,
                    title: 'Confirm Deletion'
                });

            } else if (this.modalData instanceof Service) {
                this.entityData = new DeleteEntityDataType({
                    entity: (this.modalData as Service),
                    entityType: 'service',
                    request: {
                        handle: (this.modalData as Service).handle,
                    },
                    endpoint: Endpoint.realmManager_deleteService,
                    message: `Delete "` + (this.modalData as Service).serviceName + `"? This action cannot be undone.`,
                    okLabel: 'Delete Service',
                    updateStatusType: UpdateStatusType.SERVICE,
                    useWarningStyle: true,
                    title: 'Confirm Deletion'
                });
            } else if (this.modalData instanceof Storage) {
                const currentEntity = this.modalData as Storage;
                let msg = currentEntity.isInUse ? `Storage "` + currentEntity.name + `" is in use.\n` : '';
                this.entityData = new DeleteEntityDataType({
                    entity: currentEntity,
                    entityType: 'storage',
                    request: {
                        handle: currentEntity.handle,
                        taskDescription: `Delete Storage Area ${currentEntity.handle}`,
                        systemScope: true
                    },
                    endpoint: Endpoint.realmManager_deleteStorageArea,
                    message: msg + `Delete "` + currentEntity.name + `"? This action cannot be undone.`,
                    okLabel: 'Delete Storage',
                    updateStatusType: UpdateStatusType.STORAGE,
                    useWarningStyle: true,
                    title: 'Confirm Deletion'
                });
            } else {
                console.error('Attempting to delete unknown object.  Configure delete-entity-modal.component.ts to enable this.');
            }
            this.modalTitle = this.entityData?.title;
            this.modalHelpIndex = this.entityData?.helpIndex;
            this.isBusyMessage = this.entityData?.isBusyMessage;
            this.modalClass += this.modalData instanceof RealmNode ? ' delete-realm-node' : ((this.modalData.viewType === ViewType.TAG || this.modalData instanceof Tag) && this.modalData.documentCount > 0) || this.entityData.useWarningStyle ? ' warning-style' : '';
        }
    }

    ok(): void {
        if (this.entityData && this.entityData.endpoint && this.entityData.request) {
            this.isBusy = true;
            const message = !this.options || !this.options.suppressMessage;
            if (this.modalData.viewType === ViewType.TAG) {
                this.commonService.tagNode.isLoading = true;
            }
            if (this.entityData.entityType === 'project') {
                this.restService.post(this.entityData.endpoint, this.entityData.request, true,
                    [MessageType.project, undefined]).then(response => {
                        this.commonService.sendMessage(MessageType.updateSystemSettingsProjectList);
                    });
            } else if (this.entityData.entityType === 'organization') {
                this.restService.post(this.entityData.endpoint, this.entityData.request, true,
                    [MessageType.organization, undefined]).then(response => {
                        this.commonService.sendMessage(MessageType.updateSystemSettingsOrganizationList);
                    });
            } else {
                this.restService.post(this.entityData.endpoint, this.entityData.request, true,
                    [MessageType.entityChanged, this.entityData.updateStatusType]);
            }
            this.isBusy = false;
            this.closeModal(true);
        } else {
            if (this.entityData.entityType === 'tagApplication') {
                this.closeModal(true);
            } else {
                this.closeModal(false);
            }
        }
    }

    openHelp = () => {
        if (this.entityData.helpIndex) {
            this.commonService.openHelp(this.entityData.helpIndex);
        }
    }

    get showModalBody(): boolean {
        return !(this.modalData instanceof Project) || !!this.entityData?.message;
    }
}

